// Thin fetch wrapper for the Worker API.
//
// Sign-in is username + password; the session lives in an HttpOnly cookie the
// browser sends automatically. Every call carries `X-App: 1` — the Worker rejects
// mutating requests without it (CSRF guard). In dev, VITE_DEV_EMAIL is sent as
// X-Dev-Email and trusted only when the Worker runs with ENVIRONMENT=development.

import type { RegisterData } from './model'

export interface ApiError {
  status: number
  message: string
  code: string
}

function devHeaders(): HeadersInit {
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_EMAIL) {
    return { 'X-Dev-Email': import.meta.env.VITE_DEV_EMAIL }
  }
  return {}
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    ...init,
    headers: {
      'content-type': 'application/json',
      'X-App': '1',
      ...devHeaders(),
      ...init.headers,
    },
  })

  const text = await res.text()
  const body = text ? JSON.parse(text) : {}

  if (!res.ok) {
    const err: ApiError = {
      status: res.status,
      message: body.error ?? `Request failed (${res.status})`,
      code: body.code ?? 'error',
    }
    throw err
  }
  return body as T
}

export interface Me {
  email: string
  name: string | null
  role: 'admin' | 'user'
  mustChangePassword: boolean
}

export interface Health {
  ok: true
  phase: number
  environment: string
  needsSetup: boolean
}

export const getMe = () => api<Me>('/me')
export const getHealth = () => api<Health>('/health')
export const getData = () => api<RegisterData>('/data')

// --- sign-in -----------------------------------------------------------
export const login = (email: string, password: string) =>
  api<Me>('/login', { method: 'POST', body: JSON.stringify({ email, password }) })

export const logout = () => api<{ ok: true }>('/logout', { method: 'POST' })

export const setupFirstAdmin = (email: string, name: string, password: string) =>
  api<Me>('/setup', { method: 'POST', body: JSON.stringify({ email, name, password }) })

export const changeMyPassword = (current: string, next: string) =>
  api<{ ok: true }>('/account/password', {
    method: 'POST',
    body: JSON.stringify({ current, next }),
  })

// --- users (admin) --------------------------------------------------
export interface UserRow {
  email: string
  name: string | null
  role: 'admin' | 'user'
  must_change_password: number
  last_login_at: string | null
  locked_until: string | null
  created_at: string
}
export const listUsers = () => api<{ users: UserRow[] }>('/users')
export const createUser = (email: string, name: string, role: 'admin' | 'user', tempPassword?: string) =>
  api<{ email: string; tempPassword: string; generated: boolean }>('/users', {
    method: 'POST',
    body: JSON.stringify({ email, name, role, tempPassword }),
  })
export const updateUser = (
  email: string,
  patch: { role?: 'admin' | 'user'; resetPassword?: boolean; unlock?: boolean },
) =>
  api<{ email: string; role?: string; tempPassword?: string; unlocked?: boolean }>(
    `/users/${encodeURIComponent(email)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  )
export const deleteUser = (email: string) =>
  api<{ ok: true }>(`/users/${encodeURIComponent(email)}`, { method: 'DELETE' })

// --- Phase 2: admin writes -------------------------------------------
type Body = Record<string, unknown>

export const createRecord = (type: string, body: Body) =>
  api<{ id: string }>(`/records/${type}`, { method: 'POST', body: JSON.stringify(body) })

export const updateRecord = (type: string, id: string, body: Body) =>
  api<{ id: string }>(`/records/${type}/${id}`, { method: 'PATCH', body: JSON.stringify(body) })

export const deleteRecord = (type: string, id: string) =>
  api<{ ok: true }>(`/records/${type}/${id}`, { method: 'DELETE' })

export const confirmRenewed = (type: string, id: string) =>
  api<{ id: string; last_verified_at: string }>(`/records/${type}/${id}/confirm-renewed`, {
    method: 'POST',
  })

export const exportData = () => api<Record<string, unknown>>('/export')

export const importData = (payload: Body) =>
  api<{ ok: true; created: number; updated: number }>('/import', {
    method: 'POST',
    body: JSON.stringify(payload),
  })

// --- Phase 3: encryption (admin) -----------------------------------
export const getVault = () =>
  api<{ exists: boolean; salt?: string; iterations?: number; verifier?: string }>('/vault')

export const createVault = (body: { salt: string; iterations: number; verifier: string }) =>
  api<{ ok: true }>('/vault', { method: 'POST', body: JSON.stringify(body) })

export const getSecret = (type: string, id: string) =>
  api<{ blob: string | null }>(`/records/${type}/${id}/secret`)

export const setSecret = (type: string, id: string, blob: string | null) =>
  api<{ ok: true; has_password: boolean }>(`/records/${type}/${id}/secret`, {
    method: 'PUT',
    body: JSON.stringify({ blob }),
  })

// --- Phase 4: reminders -------------------------------------------
export interface AttentionItem {
  type: string
  id: string
  name: string
  date: string
  days: number
}
export const getAttention = () =>
  api<{ pastDue: AttentionItem[]; dueSoon: AttentionItem[] }>('/attention')

// --- Phase 5: RDAP (admin) --------------------------------------
export const rdapRefresh = (id: string) =>
  api<{ id: string; name: string; from: string | null; to: string | null; changed: boolean; note?: string }>(
    `/records/domains/${id}/rdap`,
    { method: 'POST' },
  )

export const rdapRefreshAll = () =>
  api<{ updated: string[]; unchanged: string[]; skipped: string[] }>('/rdap/refresh-all', {
    method: 'POST',
  })
